import React, { useState, useCallback, useEffect, useRef } from 'react';
import { CorrelationIdHandler } from './service';
import {Tooltip, Alert, List, message } from 'antd';
import apiCalls from 'src/api/apiCalls';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import moment from 'moment';
import HeaderComponent from '../header';
import Loader from '../loader.component';

const UserActivityLogsGrid = () => {
  const [copied, setCopied] = useState('');
  const [logsData, setLogsData] = useState([]);
  const [pageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const logsDataRef = useRef([]);
  const apiUrl = `identity/security-logs`;

  const fetchTableData = useCallback(async () => {
    if (!hasMore || loading) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiCalls.paginatedApiUrl(apiUrl, pageSize, currentPage);

      if (response.ok) {
        const newItems = response?.data?.items || [];
        const total = response?.data?.totalCount || 0;

        setLogsData((prev) => [...prev, ...newItems]);
        logsDataRef.current = [...logsDataRef.current, ...newItems];

        if (logsDataRef.current.length >= total) {
          setHasMore(false);
        } else {
          setCurrentPage(prev => prev + 1);
        }
      } else {
        setError(response.error);
      }
    } catch (e) {
      setError(e.message || 'Something went wrong while fetching logs.');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, pageSize, currentPage, hasMore, loading]);

  useEffect(() => {
    fetchTableData();
  }, []);

  const handleCopy = useCallback((id) => () => {
    setCopied(id);
    setTimeout(() => setCopied(''), 300);
  }, []);

const handleExcelExport = useCallback(() => {
  try {
    const formattedData = logsData.map((item) => ({
      Date: moment.utc(item.creationTime).local().format('DD/MM/YYYY hh:mm:ss A'),
      Action: item.action,
      'IP Address': item.clientIpAddress,
      Application: item.applicationName,
      Identity: item.identity,
      'User Name': item.userName,
      // Client: item.clientId,
      'Correlation Id': item.correlationId,
    }));

    // Create worksheet and workbook
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    worksheet['!cols'] = [
      { wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 25 },
      { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 30 },
    ];
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'ActivityLogs');

    // Generate and download file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `ActivityLogs.xlsx`);
    
    message.success('Download Excel Successfully');
  } catch (error) {
    console.error('Excel export error:', error);
    message.error(`Export failed: ${error.message || 'Unknown error'}`);
  }
}, [logsData]);




  const closeErrorHandler = useCallback(() => {
    setError(null);
  }, []);
  const breadCrumbList = [
    { bname: 'Activity Logs' },
  ]
  return (
    <div className="w-full program-card">
      <HeaderComponent breadCrumbList={breadCrumbList} />
      <div className=" flex justify-end gap-2 mb-3">
        <Tooltip key={"Export Excel"} title={"Export Excel"}>
          <div className="" onClick={handleExcelExport}>
            <span className="icon lg excel-icon cursor-pointer"></span>
          </div>
        </Tooltip>
      </div>
      {error && (
        <div className="mb-4">
          <Alert
            className="mb-24"
            type="error"
            description={error}
            onClose={closeErrorHandler}
            showIcon
            closable
          />
        </div>
      )}

      <div className="overflow-x-auto border border-StrokeColor rounded-5">
        <div className='min-w-[1300px] overflow-x-auto'>
        <div className="flex w-full font-semibold bg-bodyBg text-paraColor border-b border-StrokeColor">  
         <div className="w-[200px] px-3 py-2 border-r border-StrokeColor">Date</div>
         <div className="w-[160px] px-3 py-2 border-r border-StrokeColor">Action</div>
         <div className="w-[170px] px-3 py-2 border-r border-StrokeColor">IP Address</div>
         <div className="w-[200px] px-3 py-2 border-r border-StrokeColor">Application</div>
         <div className="w-[170px] px-3 py-2 border-r border-StrokeColor">Identity</div>
         <div className="w-[160px] px-3 py-2 border-r border-StrokeColor">User Name</div>
         {/* <div className="w-[150px] px-3 py-2 border-r border-StrokeColor">Client</div> */}
         <div className="w-[240px] px-3 py-2 border-r border-StrokeColor">Correlation Id</div>
        </div>
        
        <List
          dataSource={logsData}
          renderItem={(logItem) => (
            <List.Item key={logItem.id} className='!border-b !border-StrokeColor hover:bg-kendotdBghover !text-paraColor'>
              <div className="flex w-full">
                <div className="w-[200px] truncate text-subTextColor font-normal text-sm px-3">{moment.utc(logItem.creationTime).local().format('DD/MM/YYYY hh:mm:ss A')}</div>
                <div className="w-[160px] truncate text-subTextColor font-normal text-sm px-3">{logItem.action}</div>
                <div className="w-[170px] truncate text-subTextColor font-normal text-sm px-3">{logItem.clientIpAddress}</div>
                <div className="w-[200px] truncate text-subTextColor font-normal text-sm px-3">{logItem.applicationName}</div>
                <div className="w-[170px] truncate text-subTextColor font-normal text-sm px-3">{logItem.identity}</div>
                <div className="w-[160px] truncate text-subTextColor font-normal text-sm px-3">{logItem.userName}</div>
                {/* <div className="w-[150px] truncate text-subTextColor font-normal text-sm px-3">{logItem.clientId}</div> */}
                <div className="w-[240px] truncate text-subTextColor font-normal text-sm px-3">
                  <CorrelationIdHandler
                    data={logItem}
                    handleCopy={handleCopy}
                    copied={copied}
                  />
                </div>
              </div>
            </List.Item>
          )}
          onScroll={(e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !loading) {
              fetchTableData();
            }
          }}
          style={{ maxHeight: '400px', overflow: 'auto' }}
        >
          {loading && hasMore && (
            <div className='text-center p-2'>
              <span className="loader"></span> <Loader/>
            </div>
          )}
          {!loading && !hasMore && logsData.length > 0 && (
            <div className='text-center p-2 text-paraColor'>
              Hey!, you have seen it all.
            </div>
          )}
        </List>
        </div>
      </div>
    </div>
  );
};

export default UserActivityLogsGrid;